import React, { useState, useRef } from "react";
import {
  Menu,
  Box,
  Typography,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Tooltip,
  Avatar,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedDomainSuccess } from "../../../core/events/domainEvents";
import {
  ExpandMoreRounded,
  UnfoldMoreRounded,
  CloseRounded,
} from "@mui/icons-material";
import {
  getSessionMessagesSuccess,
  getSessionsSuccess,
} from "../../../core/events/sessionEvents";
import { makeStyles } from "tss-react/mui";
import { usePostHog } from "posthog-js/react";
import {
  OPEN_SWITCH_PLANT_MENU,
  CLOSE_SWITCH_PLANT_MENU,
  SWITCH_PLANT,
} from "../../../utils/posthogEvents";

const useStyles = makeStyles()((theme) => ({
  avatar: {
    height: 37,
    width: 37,
    color: "white",
    borderRadius: 9.33,
    fontSize: 16,
    // background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)", // fallback gradient (removed)
    // The backgroundColor will be overridden by sx if provided
  },
  menuAvatar: {
    color: "white",
    borderRadius: 9.33,
    mr: 1.5,
    // background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)", // fallback gradient (removed)
  },
}));

// Darker, more vibrant pastel gradient palette
const pastelGradients = [
  ["#6a82fb", "#fc5c7d"], // blue to pink
  ["#43cea2", "#185a9d"], // teal to blue
  ["#f7971e", "#ffd200"], // orange to yellow
  ["#667eea", "#764ba2"], // indigo to purple
  ["#f857a6", "#ff5858"], // pink to red
  ["#30cfd0", "#330867"], // cyan to deep blue
  ["#5f2c82", "#49a09d"], // purple to teal
  ["#ee9ca7", "#ffdde1"], // rose to light pink
  ["#2193b0", "#6dd5ed"], // blue to light blue
  ["#b06ab3", "#4568dc"], // purple to blue
];

// Helper to get avatar text (e.g., 'U1' for 'Urea 1', 'AP' for 'Alpha Plant')
function getAvatarText(domainName) {
  if (!domainName) return "";
  const words = domainName.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Helper to get gradient background, fallback to color if present
export function getAvatarSx(domainIndex) {
  const gradient = pastelGradients[domainIndex % pastelGradients.length];
  return {
    background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
  };
}

function SelectDomain({ expanded }) {
  const posthog = usePostHog();
  const { classes } = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  // Revert to using Redux state for domains and selectedDomain
  const { domains, selectedDomain } = useSelector((state) => state.domain);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const anchorRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    posthog.capture(OPEN_SWITCH_PLANT_MENU);
  };

  const handleClose = () => {
    setAnchorEl(null);
    posthog.capture(CLOSE_SWITCH_PLANT_MENU);
  };

  const handleSelectDomain = (domain) => {
    setSelectedDomain(domain);
    handleClose();
    posthog.capture(SWITCH_PLANT, { domain: domain.domain });
  };

  function setSelectedDomain(domain) {
    dispatch(setSelectedDomainSuccess(domain));
    dispatch(getSessionsSuccess([]));
    dispatch(getSessionMessagesSuccess([]));
  }

  return (
    selectedDomain && (
      <Box px={1.5}>
        {!expanded ? (
          <Tooltip title="Switch plant" arrow placement="right">
            <Box
              ref={anchorRef}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  background: theme.palette.action.hover,
                  borderRadius: 1,
                },
              }}
              onClick={handleClick}>
              <Box display={"flex"} justifyContent={"center"}>
                <Avatar
                  className={classes.avatar}
                  sx={{
                    ...getAvatarSx(
                      domains.findIndex((d) => d.id === selectedDomain.id)
                    ),
                  }}>
                  {getAvatarText(selectedDomain["domain"])}
                </Avatar>
              </Box>
              <Box display={"flex"} alignItems={"center"} mt={0.5}>
                <Typography fontSize={12} noWrap color={"text.secondary"}>
                  {selectedDomain["domain"]}
                </Typography>
                <UnfoldMoreRounded
                  fontSize="small"
                  sx={{ color: theme.palette.action.active }}
                />
              </Box>
            </Box>
          </Tooltip>
        ) : (
          <>
            <Box
              ref={anchorRef}
              p={1}
              borderRadius={3}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: theme.palette.action.hover,
                cursor: "pointer",
                transition: "all 0.5s linear",
                "&:hover": {
                  opacity: 0.7,
                },
              }}
              onClick={handleClick}>
              <Box display={"flex"} alignItems={"center"}>
                <Avatar
                  className={classes.avatar}
                  sx={{
                    ...getAvatarSx(
                      domains.findIndex((d) => d.id === selectedDomain.id)
                    ),
                  }}>
                  {getAvatarText(selectedDomain["domain"])}
                </Avatar>
                <Box ml={1} />
                <Typography
                  noWrap
                  textOverflow={"ellipsis"}
                  color={"text.secondary"}
                  variant="body2"
                  maxWidth={expanded ? 120 : 50}
                  style={{ fontWeight: 500 }}>
                  {selectedDomain["domain"]}
                </Typography>
              </Box>
              <Box mr={1} />
              <UnfoldMoreRounded
                fontSize="small"
                sx={{ color: theme.palette.action.active }}
              />
            </Box>
          </>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                minWidth: expanded ? 180 : 200,
                mt: 1,
                width: expanded
                  ? anchorRef.current
                    ? anchorRef.current.offsetWidth
                    : undefined
                  : 200,
              },
            },
          }}>
          <Box
            mx={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Typography variant="overline" color={"textSecondary"}>
              Switch plant
            </Typography>
          </Box>
          {domains.map((d, idx) => {
            const { id, domain } = d;
            return (
              <MenuItem
                key={id}
                onClick={() => handleSelectDomain(d)}
                selected={selectedDomain["id"] === id}>
                <ListItemIcon>
                  <Avatar
                    className={classes.menuAvatar}
                    sx={{
                      ...getAvatarSx(idx),
                      color: "white",
                      borderRadius: 2,
                      mr: 1.5,
                    }}>
                    {getAvatarText(d["domain"])}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      noWrap
                      textOverflow="ellipsis"
                      color="text.secondary"
                      variant="body2"
                      maxWidth={150}
                      style={{ fontWeight: 500 }}>
                      {domain}
                    </Typography>
                  }
                />
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    )
  );
}

export default SelectDomain;
